import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Copy } from "lucide-react";
import { toast } from "sonner";

const safeCopy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label || "Texto copiado");
  } catch {
    toast.info("Nao foi possivel copiar automaticamente neste ambiente");
  }
};

const parseScript = (text) => {
  if (!text) return {};
  const sections = {};
  const regex = /(?:^|\n)\s*\[?\s*(m[1-4])\s*\]?\s*[:\-\u2014]?\s*(?:\n|$)/gim;
  let match;
  const markers = [];
  while ((match = regex.exec(text)) !== null) {
    markers.push({ key: match[1].toLowerCase().trim(), index: match.index, end: match.index + match[0].length });
  }
  if (markers.length === 0) return {};
  for (let i = 0; i < markers.length; i++) {
    const start = markers[i].end;
    const end = i + 1 < markers.length ? markers[i + 1].index : text.length;
    const content = text.slice(start, end).trim();
    if (content) sections[markers[i].key] = content;
  }
  return sections;
};

export function ScriptModal({ open, onClose, scriptText, setScriptText, onSave, saving, pipelineName }) {
  const sections = parseScript(scriptText);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-base font-semibold">
            Script — {pipelineName}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3" style={{ minHeight: 0 }}>
          <div>
            <Label className="text-xs mb-1 block">
              Script completo (use [M1], [M2], [M3], [M4] para separar fases)
            </Label>
            <Textarea
              data-testid="script-textarea"
              value={scriptText}
              onChange={(e) => setScriptText(e.target.value)}
              placeholder={"[M1]\nOla! Somos do Salao X...\n\n[M2]\nOi, tudo bem? Passando para lembrar...\n\n[M3]\nUltima chance! Seu cupom expira...\n\n[M4]\nFechamento final..."}
              className="text-xs min-h-[200px] resize-none font-mono leading-relaxed"
            />
          </div>

          {scriptText && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500 font-medium">Preview por fase:</p>
              {["m1", "m2", "m3", "m4"].map((key) => {
                const text = sections[key];
                return (
                  <div key={key} className="bg-zinc-900 rounded-md p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-semibold text-zinc-400 uppercase">{key.toUpperCase()}</span>
                      {text && (
                        <button
                          data-testid={`copy-${key}-btn`}
                          onClick={() => safeCopy(text, `${key.toUpperCase()} copiado`)}
                          className="text-[10px] text-amber-400 hover:text-amber-300 transition-colors"
                        >
                          Copiar
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-zinc-500 whitespace-pre-wrap">{text || "\u2014"}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-3 border-t border-zinc-800">
          <Button
            data-testid="script-save-btn"
            onClick={onSave}
            disabled={saving}
            className="flex-1 bg-amber-500 text-zinc-950 hover:bg-amber-600 h-8 text-xs font-semibold disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar Script"}
          </Button>
          {scriptText && (
            <Button
              data-testid="script-copy-all-btn"
              type="button"
              onClick={() => safeCopy(scriptText, "Script completo copiado")}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 h-8 text-xs"
            >
              <Copy className="h-3 w-3 mr-1" /> Copiar tudo
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
